import { useEffect, useState, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useRetryableQuery } from './useRetryableQuery';
import { logger } from '@/lib/logger';

export interface Announcement {
  id: string;
  title: string;
  content: string;
  type: 'info' | 'warning' | 'success' | 'error';
  is_active: boolean;
  is_dismissible: boolean;
  starts_at: string;
  ends_at: string | null;
  target_plans: string[];
  created_at: string;
}

interface AnnouncementData {
  announcements: Announcement[];
  dismissedIds: Set<string>;
}

export const useAnnouncements = () => {
  const { user, loading: authLoading } = useAuth();
  const [localDismissedIds, setLocalDismissedIds] = useState<Set<string>>(new Set());
  const [realtimeTrigger, setRealtimeTrigger] = useState(0);

  const queryFn = useCallback(async (): Promise<AnnouncementData> => {
    if (!user) {
      return { announcements: [], dismissedIds: new Set() };
    }

    // Fetch active announcements
    const { data: announcementsData, error: announcementsError } = await supabase
      .from('announcements')
      .select('*')
      .eq('is_active', true)
      .lte('starts_at', new Date().toISOString())
      .order('created_at', { ascending: false });

    if (announcementsError) throw announcementsError;

    // Filter out expired announcements
    const activeAnnouncements = (announcementsData || []).filter((a) => 
      !a.ends_at || new Date(a.ends_at) > new Date()
    );

    // Fetch dismissed announcements
    const { data: dismissedData } = await supabase
      .from('dismissed_announcements')
      .select('announcement_id')
      .eq('user_id', user.id);

    const dismissed = new Set((dismissedData || []).map((d) => d.announcement_id));

    // Filter out dismissed announcements and cast type
    const filteredAnnouncements = activeAnnouncements
      .filter((a) => !dismissed.has(a.id))
      .map((a) => ({
        ...a,
        type: a.type as 'info' | 'warning' | 'success' | 'error',
      }));

    return { announcements: filteredAnnouncements, dismissedIds: dismissed };
  }, [user, realtimeTrigger]);

  const { data, loading, retry } = useRetryableQuery({
    queryFn,
    enabled: !authLoading && !!user,
    maxRetries: 2,
    showToastOnError: false,
    errorMessage: 'Erreur lors du chargement des annonces',
  });

  // Subscribe to realtime changes
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('announcements-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'announcements'
        },
        () => {
          setRealtimeTrigger(prev => prev + 1);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Refetch when realtime trigger changes
  useEffect(() => {
    if (realtimeTrigger > 0) {
      retry();
    }
  }, [realtimeTrigger, retry]);

  const dismissAnnouncement = useCallback(async (announcementId: string) => {
    if (!user) return;

    try {
      await supabase
        .from('dismissed_announcements')
        .insert({ user_id: user.id, announcement_id: announcementId });

      // Update local state immediately for responsive UI
      setLocalDismissedIds(prev => new Set([...prev, announcementId]));
    } catch (error) {
      logger.error('Error dismissing announcement', error, 'useAnnouncements');
    }
  }, [user]);

  // Combine fetched dismissed IDs with locally dismissed ones
  const allDismissedIds = useMemo(() => {
    const combined = new Set(data?.dismissedIds || []);
    localDismissedIds.forEach(id => combined.add(id));
    return combined;
  }, [data?.dismissedIds, localDismissedIds]);

  // Filter out locally dismissed announcements
  const visibleAnnouncements = useMemo(() => {
    return (data?.announcements || []).filter(a => !allDismissedIds.has(a.id));
  }, [data?.announcements, allDismissedIds]);

  return {
    announcements: visibleAnnouncements,
    loading: authLoading || loading,
    dismissAnnouncement,
  };
};
